// ============================================================
// STRUCTURES - the shapes cubes do badly, and the only things on the
// map nothing could ever reach.
//
// A preset is built from parametric solids: round pillars, cones, hex
// pads. Every build tool writes VOXELS or flat zone solids, so
// `terrain.solids` was touched by exactly one thing - `clearTerrain`,
// which wipes the map. There was even a `selectedSolidId` outline in the
// renderer that nothing anywhere set. So the only way to remove a
// preset's cylinder was to nuke everything (v8.1.0).
//
// One primitive covers all of them - see `types.ts`, which has always
// documented the mapping. This module is the vocabulary on top of it.
//
// Pure. Zero React dependency.
// ============================================================
import { makeSolid } from './solids';
import { materialAp, derivePassability } from './voxels';
import type { Material } from './voxels';
import type { Solid } from './types';

export interface StructureShape {
  key: string;
  label: string;
  /** Vertices in the cross-section. */
  sides: number;
  /** Tapers to a point instead of standing straight. */
  tapered?: true;
  hint: string;
}

/**
 * ROUND AND TAPERED, because that is what voxels cannot do.
 *
 * A box is here too - not because cubes cannot make one, but because a
 * single solid is far cheaper than a wall's worth of meshed cells when
 * all you want is a plinth.
 */
export const STRUCTURE_SHAPES: StructureShape[] = [
  { key: 'box', label: 'Box', sides: 4, hint: 'A plinth, in one solid' },
  { key: 'cylinder', label: 'Cylinder', sides: 24, hint: 'A round pillar' },
  { key: 'hex', label: 'Hex', sides: 6, hint: 'A six-sided pillar' },
  { key: 'cone', label: 'Cone', sides: 24, tapered: true, hint: 'Round, tapering to a point' },
  { key: 'pyramid', label: 'Pyramid', sides: 4, tapered: true, hint: 'Four-sided, tapering to a point' },
];

/** How a structure's id is told from a meshed cube's. */
const PREFIX = 'str-';

let counter = 0;

/**
 * A fresh id.
 *
 * A counter, never `Math.random()`: E2E replaces the dice source with a
 * seeded sequence, and a stray consumer silently shifts every later roll
 * (gotchas, v6.45.0). The counter alone is enough - a structure keeps
 * the id it was minted with for as long as the map holds it, and two
 * maps never share a list.
 */
function nextId(): string {
  counter += 1;
  return `${PREFIX}${counter.toString(36)}`;
}

/** Is this one of ours, rather than a meshed cube or a zone? */
export function isStructure(solid: Solid | undefined | null): boolean {
  return !!solid?.id?.startsWith(PREFIX);
}

export interface StructureSpec {
  shape: string;
  x: number;
  z: number;
  /** Apothem in metres - axis to the middle of a face. */
  radius?: number;
  height?: number;
  /** Degrees about the vertical axis. */
  rotation?: number;
  /** Where its base sits, for something on a rooftop. */
  y?: number;
  material?: Material;
}

/**
 * One structure, made of a material.
 *
 * Everything a structure DOES comes from what it is made of and how it
 * was built, exactly as it does for cubes: hardness sets the cover AP,
 * transparency sets sight, permeability and height set the going. There
 * is nothing here to assign by hand and so nothing that can disagree
 * with itself.
 */
export function makeStructure(spec: StructureSpec): Solid {
  const shape = STRUCTURE_SHAPES.find((s) => s.key === spec.shape) ?? STRUCTURE_SHAPES[0];
  const material = spec.material;
  const radius = spec.radius ?? 1;
  const height = spec.height ?? 3;
  return makeSolid({
    id: nextId(),
    sides: shape.sides,
    radiusBottom: radius,
    radiusTop: shape.tapered ? 0 : radius,
    scaleX: 1,
    scaleZ: 1,
    height,
    rotation: spec.rotation ?? 0,
    position: { x: spec.x, y: spec.y ?? 0, z: spec.z },
    // Hardness sets the AP; the LEVEL is worked out per shot, because it
    // depends on the size of whoever is being shot at.
    cover: { level: 'none', ap: materialAp(material) },
    coverMode: 'derived',
    transparency: material?.transparency ?? 0,
    opaque: (material?.transparency ?? 0) === 0,
    ...(material?.colour ? { colour: material.colour } : {}),
    terrain: derivePassability(height, material?.permeability ?? 0),
    ...(material?.permeability ? { permeability: material.permeability } : {}),
    ...(material?.hidden ? { hidden: true as const } : {}),
    ...(material?.name ? { label: material.name } : {}),
  });
}

/** Put one on the map. Returns a NEW list - nothing here mutates. */
export function addStructure(solids: Solid[], solid: Solid): Solid[] {
  return [...solids, solid];
}

/**
 * Change one, by id.
 *
 * Returns the SAME array when the id is not there, so a caller can rely
 * on identity to know whether anything happened - which is what stops a
 * miss from re-rendering the whole scene.
 */
export function updateStructure(solids: Solid[], id: string, patch: Partial<Solid>): Solid[] {
  if (!solids.some((s) => s.id === id)) return solids;
  return solids.map((s) => (s.id === id ? { ...s, ...patch } : s));
}

/** Take one away. The same array back when there was nothing to take. */
export function removeStructure(solids: Solid[], id: string): Solid[] {
  if (!solids.some((s) => s.id === id)) return solids;
  return solids.filter((s) => s.id !== id);
}
